// Mirrors the C# DTOs in BaseDashboard/SceneDesigner/Endpoints/SceneDesignerEmbedEndpoints.cs.

export interface SceneCatalogResponse {
  categories: SceneCategoryDto[];
  products: SceneProductDto[];
  viewer: SceneViewerDto | null;
}

export interface SceneCategoryDto {
  id: number;
  parentId: number | null;
  name: string;
  description: string;
  iconUrl: string | null;
  sortOrder: number;
  /** Direct (non-recursive) product members. */
  productIds: string[];
}

export interface SceneProductDto {
  id: string;
  name: string;
  imageUrl: string | null;
  modelAssetId: string | null;
  /** Resolved parameter values keyed by parameter definition's `key`.
   *  Null when the product has no parameter values. The catalog block reads
   *  from this to render its spec subtitle. */
  parameterValues: Record<string, string> | null;
  /** Surface key → material collection id for every Collection-bound surface
   *  on this product. Used by the drag layer to resolve the user's
   *  left-panel collection picks into a per-surface binding list at drag-
   *  start time (so a fresh spawn carries the picked defaults). Null when
   *  the product has no collection-bound surfaces. */
  surfaceCollections?: Record<string, string> | null;
}

export interface SceneViewerDto {
  target: string;
  version: string;
  buildUrl: string;
  buildName: string;
  accessToken: string | null;
}

/** Response of GET /api/embed/scene-designer/product/{productId}. */
export interface SceneProductDetailResponse {
  id: string;
  name: string;
  surfaces: SceneSurfaceDto[];
  /** Variant keys the product's spawn bundle declares (e.g. ["main", "open"]).
   *  React uses this to disable the toggle-variant button when the target
   *  variant isn't available on the selected product. Always includes "main"
   *  (or whatever the default variant is) when variants are present;
   *  empty / absent means the product has no variants at all. */
  variants?: string[];
}

export interface SceneSurfaceDto {
  surfaceKey: string;
  surfaceLabel: string;
  /** Fixed finish — applied at spawn, not user-pickable; hidden from the picker. */
  locked: boolean;
  defaultMaterialId: string | null;
  materials: SceneMaterialDto[];
}

// ProductID resolve types moved to src/api/productIdClient.ts — shared with the
// Viewer. Re-exported here as aliases so existing SceneDesigner imports keep
// working without touching every call site.
export type {
  ProductIdResolveRequest as ProductIdsRequest,
  ProductIdResolveItem as ProductIdsItem,
  ProductIdResolveResponse as ProductIdsResponse,
  ProductIdResolveItemResult as ProductIdsItemResult,
} from "../../api/productIdClient";

export interface SceneMaterialDto {
  /** Material row id — picker state key. */
  id: string;
  /** DMatAsset id — what a SetSurfaceMaterials binding's dmatId must carry. */
  dmatAssetId: string | null;
  name: string;
  swatchUrl: string | null;
}

/**
 * Response of GET /api/embed/scene-designer/designer/{designerId} — the resolved
 * Designer config + the catalog scoped to its series. The catalog/products/viewer
 * shapes match the bare-catalog endpoint, so the existing rendering pipeline
 * inside the React tool doesn't change.
 */
export interface DesignerConfigResponse {
  id: string;
  name: string;
  series: DesignerSeriesDto[];
  categories: SceneCategoryDto[];
  products: SceneProductDto[];
  viewer: SceneViewerDto | null;
  /** Named layout template — picks which React layout component renders the
   *  configurator. Server always sends a value; defaults to "standard". */
  layoutTemplate: string;
  /** Raw object from the server — shape matches SceneDesignerLayout below. */
  layoutToggles: Partial<SceneDesignerLayout> | null;
  /** Designer override of the layout's slot mapping. Raw JSON; the React
   *  side validates per-block at render. Null = layout defaults. */
  slotMapping: Record<string, unknown> | null;
  /** Resolved thumbnail URL of the Designer's brand-logo asset. Null when
   *  the Designer doesn't pin one. Layouts that have a brand slot render it. */
  brandLogoUrl: string | null;
  /** Concrete decimal separator ("." or ",") for formatting decimals shown to
   *  the end user (e.g. the measurement readout). The server resolves the
   *  Designer's auto/./, setting against the active language. */
  decimalSeparator: string;
  /** Entrance/scroll animation style for the configurator, authored per-Designer:
   *  null/"none" = static (default), else "fade" | "rise" | "glide". Drives a root
   *  `sd--anim sd--anim-<style>` modifier class; the CSS honours prefers-reduced-motion. */
  animationStyle?: string | null;
  theme: DesignerThemeDto;
}

export interface DesignerSeriesDto {
  id: string;
  rootCategoryId: number;
  label: string;
  /** Resolved series description (from the per-series DescriptionDvarId).
   *  Null when none set. Editorial picker renders it under the series name. */
  description: string | null;
  iconUrl: string | null;
  /** Per-series hero photograph (1024-wide thumbnail). Null falls back to
   *  iconUrl for layouts that render plain pickers. */
  heroImageUrl: string | null;
  /** Material collections attached to this series — feeds MaterialCollectionsBlock. */
  materialCollections: SeriesMaterialCollectionDto[];
}

export interface SeriesMaterialCollectionDto {
  id: string;
  label: string;
  materials: SceneMaterialDto[];
}

export interface DesignerThemeDto {
  template: string;
  /** Merged CSS-variable tokens (template defaults + per-Designer overrides). */
  tokens: Record<string, string>;
  /** Webfont import URL declared on the theme. The embed root injects it as
   *  @import url(...); above the token block. Filtered to https Google Fonts. */
  fontImportUrl?: string | null;
}

/**
 * A product placed in the scene. React owns this — the canonical scene state.
 * `instanceId` is minted by the JS drag layer and echoed back by Unity's
 * lifecycle events; `productId` is the catalog id (shared by every copy of the
 * same product, informational only).
 */
export interface SceneInstance {
  instanceId: string;
  productId: string;
  /** Per-surface material picks (surfaceKey → material row id). */
  materialPicks: Record<string, string>;
  /** Current variant on this instance. Default is the product's main variant —
   *  encoded as undefined here so existing instances without the field still
   *  represent "main". The toggle-variant action flips this between the
   *  block's configured key and undefined. */
  variantKey?: string;
  /** Designer-assigned group this instance belongs to. The Designer tracks all
   *  placed products, so grouping is a Designer concept; some use cases learn
   *  membership from Unity (which products are physically connected), others
   *  assign it directly. Undefined = ungrouped. Drives "designer-group"
   *  grouping in the product table / cart. */
  groupId?: string;
  /** Human-readable label for {@link groupId}, shown in group header rows. */
  groupName?: string;
}

/**
 * Per-subcomponent toggles. Each panel in the SceneDesigner layout checks its
 * flag here before rendering, which is the seam projects will eventually drive
 * from a dashboard-managed config row.
 */
export interface SceneDesignerLayout {
  showCatalog: boolean;
  showTopBar: boolean;
  showSideMenu: boolean;
  showSummary: boolean;
  /** The material-picker panel that slides in over the catalog on selection. */
  showSelection: boolean;
  /** Element-list cart (grouped read-only summary of placed products). */
  showCart: boolean;
}

export const defaultLayout: SceneDesignerLayout = {
  showCatalog: true,
  showTopBar: true,
  showSideMenu: true,
  showSummary: true,
  showSelection: true,
  showCart: true,
};
